// Financial Reporting suite — TanStack Query hooks over
// financialReportingApi. Scope keys: loan overviews hang off the loan key,
// the portfolio overview + series catalog are global.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  financialReportingApi,
  type CreateDwReportInstanceRequest,
  type CreateReportInstanceRequest,
  type CreateVgReportInstanceRequest,
  type UpdateSeriesActivationRequest,
} from "@/api/portfolioAdmin/financialReportingApi";
import { invalidateLoanCalendar, invalidateLoanHealth } from "./invalidation";

export const financialReportingKeys = {
  series: ["portfolio-admin", "reporting", "series"] as const,
  catalog: ["portfolio-admin", "reporting", "catalog"] as const,
  portfolioOverview: ["portfolio-admin", "reporting", "portfolio-overview"] as const,
  loanOverview: (loanId: string) =>
    ["portfolio-admin", "loan", loanId, "reporting-overview"] as const,
};

export function useReportSeries() {
  return useQuery({
    queryKey: financialReportingKeys.series,
    queryFn: () => financialReportingApi.series(),
  });
}

/** Admin catalog (incl. Dormant). Only fetched when enabled — the endpoint
 *  is gated FinancialReporting.ManageSeriesCatalog. */
export function useReportSeriesCatalog(enabled: boolean) {
  return useQuery({
    queryKey: financialReportingKeys.catalog,
    queryFn: () => financialReportingApi.catalog(),
    enabled,
  });
}

export function useUpdateSeriesActivation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, body }: { code: string; body: UpdateSeriesActivationRequest }) =>
      financialReportingApi.updateActivation(code, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: financialReportingKeys.series });
      void qc.invalidateQueries({ queryKey: financialReportingKeys.catalog });
      void qc.invalidateQueries({ queryKey: financialReportingKeys.portfolioOverview });
    },
  });
}

export function useLoanReportingOverview(loanId: string | undefined) {
  return useQuery({
    queryKey: financialReportingKeys.loanOverview(loanId ?? ""),
    queryFn: () => financialReportingApi.loanOverview(loanId!),
    enabled: !!loanId,
  });
}

export function usePortfolioReportingOverview() {
  return useQuery({
    queryKey: financialReportingKeys.portfolioOverview,
    queryFn: () => financialReportingApi.portfolioOverview(),
  });
}

function useInvalidateReportingScope(loanId: string | null) {
  const qc = useQueryClient();
  return () => {
    if (loanId) {
      void qc.invalidateQueries({
        queryKey: financialReportingKeys.loanOverview(loanId),
      });
      // The Reporting health light reads the same overview facts.
      void invalidateLoanHealth(qc, loanId);
    } else {
      void qc.invalidateQueries({
        queryKey: financialReportingKeys.portfolioOverview,
      });
    }
  };
}

/** Create a Draft instance. loanId null = portfolio-level. */
export function useCreateReportInstance(loanId: string | null) {
  const invalidate = useInvalidateReportingScope(loanId);
  return useMutation({
    mutationFn: (body: CreateReportInstanceRequest) =>
      loanId
        ? financialReportingApi.createLoanInstance(loanId, body)
        : financialReportingApi.createPortfolioInstance(body),
    onSuccess: invalidate,
  });
}

export function useSubmitReportInstance(loanId: string | null) {
  const invalidate = useInvalidateReportingScope(loanId);
  return useMutation({
    mutationFn: (reportId: string) => financialReportingApi.submit(reportId),
    onSuccess: invalidate,
  });
}

export function useCertifyReportInstance(loanId: string | null) {
  const invalidate = useInvalidateReportingScope(loanId);
  return useMutation({
    mutationFn: (reportId: string) => financialReportingApi.certify(reportId),
    onSuccess: invalidate,
  });
}

export function useFileReportInstance(loanId: string | null) {
  const qc = useQueryClient();
  const invalidate = useInvalidateReportingScope(loanId);
  return useMutation({
    mutationFn: (reportId: string) => financialReportingApi.file(reportId),
    onSuccess: () => {
      invalidate();
      // Filing advances the series' calendar tickler.
      if (loanId) void invalidateLoanCalendar(qc, loanId);
    },
  });
}

/** DW-M1 preparer path (Defaults.Manage) — used by the Defaults tab card. */
export function useCreateDwReportInstance(loanId: string) {
  const invalidate = useInvalidateReportingScope(loanId);
  return useMutation({
    mutationFn: (body: CreateDwReportInstanceRequest) =>
      financialReportingApi.createDwInstance(loanId, body),
    onSuccess: invalidate,
  });
}

export function useSubmitDwReportInstance(loanId: string) {
  const invalidate = useInvalidateReportingScope(loanId);
  return useMutation({
    mutationFn: (reportId: string) => financialReportingApi.submitDwInstance(reportId),
    onSuccess: invalidate,
  });
}

/** VG-M1 preparer path (Vendors.Manage) — used by the Goals tab card. */
export function useCreateVgReportInstance(loanId: string) {
  const invalidate = useInvalidateReportingScope(loanId);
  return useMutation({
    mutationFn: (body: CreateVgReportInstanceRequest) =>
      financialReportingApi.createVgInstance(loanId, body),
    onSuccess: invalidate,
  });
}

export function useSubmitVgReportInstance(loanId: string) {
  const invalidate = useInvalidateReportingScope(loanId);
  return useMutation({
    mutationFn: (reportId: string) => financialReportingApi.submitVgInstance(reportId),
    onSuccess: invalidate,
  });
}
